/**
 * Route → document.title mapping (UX P2, 2026-07-28: every tab used to
 * read "Triage Dashboard", making multi-tab triage sessions and browser
 * history unscannable).
 *
 * Pure function so the mapping is unit-testable; installed by the
 * `TitleInstaller` component in `App.tsx` (a `useLocation` effect —
 * fires on every navigation, same shape as `TelemetryInstaller`).
 *
 * Design choices:
 * - Section names mirror `TopNav.tsx::SECTIONS` labels so the tab title
 *   matches what the operator clicked.
 * - Detail pages carry a short (8-char) identifier — enough to tell two
 *   analysis tabs apart without quoting a full UUID in the title.
 * - Unknown paths fall back to the base title rather than guessing —
 *   a new route without a title entry is scannable-but-generic, never
 * wrong. Adding the entry here (+ a test) is part of adding a route.
 */

import { decodePathSegment } from "./user-link";

const BASE = "Triage";

/** First 8 chars of a path segment, for UUID-ish identifiers. */
function short(segment: string | undefined): string {
  return (segment ?? "").slice(0, 8);
}

export function titleForPath(pathname: string): string {
  // Normalize: strip trailing slash (except root), drop query/hash
  // (callers pass `location.pathname`, which never carries them).
  const path =
    pathname.length > 1 && pathname.endsWith("/")
      ? pathname.slice(0, -1)
      : pathname;
  const seg = path.split("/").filter(Boolean);

  if (path === "/") return `Home — ${BASE}`;
  if (path === "/login") return `Sign in — ${BASE}`;

  switch (seg[0]) {
    case "analyses":
      if (seg.length >= 3 && seg[2] === "conversation") {
        return `Conversation ${short(seg[1])} — ${BASE}`;
      }
      if (seg.length >= 2) return `Analysis ${short(seg[1])} — ${BASE}`;
      break;
    case "bundles":
      return seg.length >= 2
        ? `Bundle ${short(seg[1])} — ${BASE}`
        : `Bundles — ${BASE}`;
    case "commits":
      return seg.length >= 2
        ? `Commit ${short(seg[1])} — ${BASE}`
        : `Commits — ${BASE}`;
    case "duplicates":
      if (seg.length >= 2) return `Duplicates ${short(seg[1])} — ${BASE}`;
      break;
    case "fixes":
      // Galley #34714: Recurring fixes, in the Fixes dropdown after Commits.
      if (seg.length === 2 && seg[1] === "recurring") return `Recurring fixes — ${BASE}`;
      // Galley #36384: Problems, after Recurring fixes.
      if (seg.length === 2 && seg[1] === "problems") return `Problems — ${BASE}`;
      // A problem's own page: its id is opaque, so the tab says what kind of page it is.
      if (seg.length === 3 && seg[1] === "problems") return `Problem — Problems — ${BASE}`;
      break;
    case "alerts":
      return seg.length >= 2
        ? `Alert #${seg[1]} — ${BASE}`
        : `Alerts — ${BASE}`;
    case "trends":
      // Story / shake detail pages stay under the Trends umbrella —
      // theme ids are opaque numbers that don't aid tab scanning.
      return `Trends — ${BASE}`;
    case "remediations":
      // TopNav labels this section "Remediations" (Fixes dropdown).
      return `Remediations — ${BASE}`;
    case "feedback":
      return `Feedback — ${BASE}`;
    case "activity":
      return `Activity — ${BASE}`;
    case "users":
      // Users page (TRIAG-427): profiles carry the app username, decoded with
      // a decode that can't throw — TitleInstaller sits OUTSIDE every
      // ErrorBoundary, so a pasted `/users/100%real` must not blank the app.
      // Not `short()`: usernames aren't UUIDs.
      return seg.length >= 2
        ? `${decodePathSegment(seg[1] ?? "")} — Users — ${BASE}`
        : `Users — ${BASE}`;
    case "subscriptions":
      return `My subscriptions — ${BASE}`;
    case "scheduled-reports":
      return `Scheduled reports — ${BASE}`;
    case "chat":
      return `Chat — ${BASE}`;
    case "hardware":
      // Hardware page (TRIAG-429): one flat route; views are query params.
      return `Hardware — ${BASE}`;
    case "search":
      // Galley #34784: each tab is named after the page's own heading.
      return `Search findings — ${BASE}`;
    case "operator":
      return `Feedback about triage — ${BASE}`;
    case "chart":
      return `Reports over time — ${BASE}`;
    case "status":
      return `Status — ${BASE}`;
    case "link":
      return `Link Slack — ${BASE}`;
    case "admin": {
      const sub: Record<string, string> = {
        llm: "LLM",
        usersnap: "Usersnap",
        mcp: "MCP",
        "page-views": "Page views",
        feedback: "Feedback",
      };
      const label = seg.length >= 2 ? sub[seg[1] ?? ""] : undefined;
      return label ? `Admin · ${label} — ${BASE}` : `Admin — ${BASE}`;
    }
    default:
      break;
  }
  // Unknown route: keep the historical base title (never wrong, just
  // generic). New routes should add a mapping + test here.
  return "Triage Dashboard";
}
